import { InputAdornment, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { useState } from "react";
import {
  CountryIso2,
  defaultCountries,
  FlagEmoji,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import { setReduxPhone, useOnboarding } from "src/store/features/onboarding.slice";
import { useUserSlice } from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import { StyledTextField } from "./styled-components/AddressFields";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { FormValues } from "./utils/formSchema";

export const MuiPhone = () => {
  const dispatch = useAppDispatch();
  const [phoneChanged, setPhoneChanged] = useState(false);
  const {
    details: { phone: accountPhone },
  } = useUserSlice();
  /** Lock only when signup/account already supplied a phone (field is pre-filled). Legacy signups may have no stored phone — keep editable. */
  const isPhoneLocked = Boolean(accountPhone?.trim());

  const { phone: userPhone } = useOnboarding();
  const { phone, handlePhoneValueChange, country, setCountry } = usePhoneInput({
    countries: defaultCountries,
    value: userPhone ?? "",
    onChange: (data) => {
      if (isPhoneLocked) return;
      const modifiedValue = data.phone.replace(/[\s()-+]/g, "");
      setPhoneChanged(modifiedValue?.length > 10 ? true : false);
      dispatch(setReduxPhone(data.phone));
    },
  });

  const modifiedPhone = phone.replace(/[\s()-+]/g, "");
  const formik = useFormikContext<FormValues>();
  const {
    errors: { phone: errorMessage },
  } = formik;

  const lockedTooltipTitle =
    "This number was saved when you signed up and cannot be changed here. If you would like to update it, please contact us at support@calebandbrown.com after completing your verification.";

  const showValidTick = phoneChanged || isPhoneLocked;

  const phoneField = (
    <Field
      sx={{
        "& .MuiOutlinedInput-notchedOutline": {
          ...(!isPhoneLocked && modifiedPhone?.length > 10 ? { border: "1px solid #0BEC9F" } : {}),
        },
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused fieldset": {
            ...(!isPhoneLocked && modifiedPhone?.length > 10
              ? { border: "1px solid #0BEC9F" }
              : {}),
          },
        },
      }}
      value={phone}
      color="primary"
      name="phone"
      lable="Phone"
      error={!!errorMessage}
      helperText={errorMessage}
      disabled={isPhoneLocked}
      as={StyledTextField}
      variant="outlined"
      placeholder="Phone number"
      onChange={handlePhoneValueChange}
      type="tel"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" style={{ marginRight: "2px", marginLeft: "-8px" }}>
            <Select
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                style: {
                  height: "300px",
                  width: "100%",
                },
                sx: {
                  "& .MuiPaper-root": {
                    marginLeft: { xs: "10px", sm: "0px" },
                  },
                },
              }}
              disableUnderline
              variant="standard"
              value={country}
              disabled={isPhoneLocked}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(value) => (
                <FlagEmoji iso2={value} style={{ display: "flex", width: "20px" }} />
              )}>
              {defaultCountries.map((c) => {
                const country = parseCountry(c);
                return (
                  <MenuItem key={country.iso2} value={country.iso2}>
                    <FlagEmoji iso2={country.iso2} style={{ marginRight: "8px", width: "20px" }} />
                    <Typography marginRight="8px">{country.name}</Typography>
                    <Typography color="gray">+{country.dialCode}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <div style={{ display: showValidTick ? "flex" : "none" }}>
              <GridCheckCircleIcon sx={{ fill: "#002035", fontSize: "16px" }} />
            </div>
          </InputAdornment>
        ),
      }}
    />
  );

  if (isPhoneLocked) {
    return (
      <Tooltip title={lockedTooltipTitle} placement="top" arrow describeChild>
        <span style={{ display: "block", width: "100%" }}>{phoneField}</span>
      </Tooltip>
    );
  }

  return phoneField;
};
